import { useState } from 'react';
import { OTPInput } from '@/components/auth/OTPInput';
import { sendOTPForSettings, verifyOTPForSettings } from '@/services/settings';

interface NewEmailInputProps {
  currentEmail: string;
  userName: string;
  onVerified: (newEmail: string) => void;
  onCancel: () => void;
  onBack: () => void;
}

export function NewEmailInput({ 
  currentEmail, 
  userName, 
  onVerified, 
  onCancel: _onCancel,
  onBack
}: NewEmailInputProps) {
  const [newEmail, setNewEmail] = useState('');
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState<number | undefined>();
  const [nextAllowedAt, setNextAllowedAt] = useState<string | undefined>();


  const handleEmailChange = (value: string) => {
    setNewEmail(value);
    setEmailError('');
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!email.trim()) {
      return '새 이메일 주소를 입력해주세요.';
    }
    
    if (!emailRegex.test(email)) {
      return '올바른 이메일 형식을 입력해주세요.';
    }
    
    if (email === currentEmail) {
      return '현재 이메일과 다른 주소를 입력해주세요.';
    }
    
    return '';
  };

  const handleSendOTP = async () => {
    const validationError = validateEmail(newEmail);
    if (validationError) {
      setEmailError(validationError);
      return;
    }

    setIsResending(true);
    setOtpError('');
    setEmailError('');

    try {
      const result = await sendOTPForSettings(newEmail, 'change_email', userName);
      
      if (result.success) {
        setShowOTPInput(true);
        setRemainingAttempts(result.data?.remainingAttempts);
      } else {
        setOtpError(result.error || '인증번호 발송에 실패했습니다.');
        setRemainingAttempts(result.data?.remainingAttempts);
        setNextAllowedAt(result.data?.nextAllowedAt);
      }
    } catch (error) {
      console.error('OTP send error:', error);
      setOtpError('인증번호 발송 중 오류가 발생했습니다.');
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyOTP = async (otp: string) => {
    setIsOTPLoading(true);
    setOtpError('');

    try {
      const result = await verifyOTPForSettings(newEmail, otp, 'change_email');
      
      if (result.success) {
        onVerified(newEmail);
      } else {
        setOtpError(result.error || '인증번호가 올바르지 않습니다.');
        setRemainingAttempts(result.data?.remainingAttempts);
      }
    } catch (error) {
      console.error('OTP verify error:', error);
      setOtpError('인증번호 확인 중 오류가 발생했습니다.');
    } finally {
      setIsOTPLoading(false);
    }
  };

  const handleBackToEmailInput = () => {
    setShowOTPInput(false);
    setOtpError('');
    setRemainingAttempts(undefined);
    setNextAllowedAt(undefined);
  };

  return (
    <div>
      <div className="text-center mb-6">
        <h4 className="text-base font-semibold text-gray-900 mb-2">
          새 이메일 입력
        </h4>
        <p className="text-sm text-gray-600">
          변경할 새로운 이메일 주소를 입력하고 인증해주세요
        </p>
      </div>

      {!showOTPInput ? (
        <div className="space-y-4">
          {/* 현재 이메일 표시 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              현재 이메일
            </label>
            <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-600 text-sm">
              {currentEmail}
            </div>
          </div>

          {/* 새 이메일 입력 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              새 이메일 주소
            </label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => handleEmailChange(e.target.value)}
              placeholder="새로운 이메일 주소를 입력하세요"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4CD964] focus:border-[#4CD964] ${
                emailError ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isResending}
            />
            {emailError && (
              <p className="text-red-500 text-sm mt-1">{emailError}</p>
            )}
          </div>

          {/* 에러 메시지 */}
          {otpError && (
            <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
              {otpError}
            </div>
          )}
          
          <div className="flex gap-3 pt-2">
            <button
              onClick={onBack}
              className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              이전
            </button>
            
            <button
              onClick={handleSendOTP}
              disabled={isResending || !newEmail.trim()}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                !isResending && newEmail.trim()
                  ? 'bg-[#4CD964] text-white hover:bg-[#45B85C]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isResending ? '발송 중...' : '인증번호 받기'}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="text-sm text-gray-600 mb-4 text-center">
            <strong>{newEmail}</strong>로 발송된 인증번호를 입력해주세요
          </div>
          
          <OTPInput
            onComplete={handleVerifyOTP}
            onResend={handleSendOTP}
            isLoading={isOTPLoading}
            isResending={isResending}
            error={otpError}
            remainingAttempts={remainingAttempts}
            nextAllowedAt={nextAllowedAt}
          />
          
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleBackToEmailInput}
              className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              이메일 수정
            </button>
            
            <button
              onClick={onBack}
              className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              처음으로
            </button>
          </div>
        </div>
      )}
    </div>
  );
}